/**
 * description: manage utils used on website
 * @author CorsoR
 */

import { html, render } from 'lit-html'
import { loadWebComponents } from 'Utils/LoadResources'

const FOCUSABLE_SELECTOR =
	'button, a[href], input, select, textarea, details, [tabindex="0"], [contenteditable="true"], [role="checkbox"], [role="radio"]'

const getSafeCulture = () => `${yTos.navigation.Language}-${yTos.navigation.SiteCode.split('_')[1]}`

/**
 * method: isVisible   {check this elements is visible on style}
 * @param {String|Object} element =>  selector where you want check is visible
 * @param {Object} wrapper  => default <body> wrapper container element
 */
const isVisible = (element, wrapper = document.body) => {
	let targetElement = typeof element === 'string' ? wrapper.querySelector(element) : element
	let elementStyleVisibility = targetElement.currentStyle
		? targetElement.currentStyle.visibility
		: getComputedStyle(targetElement, null).visibility
	let elementStyleDisplay = targetElement.currentStyle
		? targetElement.currentStyle.display
		: getComputedStyle(targetElement, null).display

	if (elementStyleVisibility === 'hidden' || elementStyleDisplay === 'none') {
		return false
	}

	while (!/body/i.test(targetElement.tagName)) {
		targetElement = targetElement?.parentNode
		elementStyleVisibility = targetElement?.currentStyle
			? targetElement.currentStyle.visibility
			: getComputedStyle(targetElement, null).visibility
		elementStyleDisplay = targetElement?.currentStyle
			? targetElement.currentStyle.display
			: getComputedStyle(targetElement, null).display
		if (elementStyleVisibility === 'hidden' || elementStyleDisplay === 'none') {
			return false
		}
	}

	return true
}

/**
 * @const
 * @method getFocusableElements Check this elements is visible on style
 * @param {Object} wrapper => default <body> wrapper container element
 * @param {Boolean} onlyVisible => get focusable elements only visible on DOM
 */
const getFocusableElements = (wrapper = document.body, onlyVisible = false) => {
	let elemensFocusable = wrapper?.querySelectorAll(FOCUSABLE_SELECTOR)

	if (onlyVisible && elemensFocusable) {
		elemensFocusable = [...elemensFocusable].filter((element) => isVisible(element))
	}

	return elemensFocusable
}

const isValidEmail = (email) => {
	if (email === '') return false

	// eslint-disable-next-line no-useless-escape
	return /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^\\/<>()[\]\.,;:\s@\"]{2,})$/.test(
		email
	)
}

const isElementVisible = (el) => {
	const rect = el.getBoundingClientRect()
	const vWidth = window.innerWidth || document.documentElement.clientWidth
	const vHeight = window.innerHeight || document.documentElement.clientHeight
	const efp = (x, y) => document.elementFromPoint(x, y)

	// Return false if it's not in the viewport
	if (rect.right < 0 || rect.bottom < 0 || rect.left > vWidth || rect.top > vHeight) return false

	// Return true if any of its four corners are visible
	return (
		el.contains(efp(rect.left, rect.top)) ||
		el.contains(efp(rect.right, rect.top)) ||
		el.contains(efp(rect.right, rect.bottom)) ||
		el.contains(efp(rect.left, rect.bottom))
	)
}

/**
 * Set viewport height css variable (--vh)
 * Variable is the 0.01 of window innerHeight, it change on event mediaChanged
 */
const setViewportHeightCssVariable = () => {
	const setCssVariable = () => {
		const vh = window.innerHeight * 0.01
		document.documentElement.style.setProperty('--vh', `${vh}px`)
	}

	setCssVariable()

	yTos.watch({
		'CAR::mediaChanged': () => {
			setTimeout(setCssVariable, 0)
		},
	})
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 *
 * @param {Function} callback
 * @param {Number} delay
 */
const debounce = (callback, delay = 250) => {
	let timeoutId
	return (...args) => {
		clearTimeout(timeoutId)
		timeoutId = setTimeout(() => {
			timeoutId = null
			callback(...args)
		}, delay)
	}
}

const parseHtmlFromString = (textString, renderOuter = false) => {
	try {
		const content = new DOMParser().parseFromString(textString, 'text/html')
		return renderOuter ? content.documentElement.outerHTML : content.documentElement.textContent
	} catch (e) {
		return textString
	}
}

/**
 * getSiblings
 *
 * @description return a list of the siblings of the given element
 * @param {HTMLElement} el
 * @return {Array<HTMLElement>} siblings
 */
const getSiblings = (el) => {
	// Setup siblings array and get the first sibling
	const siblings = []
	let sibling = el.parentNode.firstChild

	// Loop through each sibling and push to the array
	while (sibling) {
		if (sibling.nodeType === 1 && sibling !== el) {
			siblings.push(sibling)
		}
		sibling = sibling.nextSibling
	}

	return siblings
}

const isIOS = () => {
	if (/iPad|iPhone|iPod/.test(navigator.platform)) {
		return true
	}
	return (
		navigator.maxTouchPoints &&
		navigator.maxTouchPoints > 2 &&
		/MacIntel/.test(navigator.platform)
	)
}

const isSafari = () => {
	if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
		return true
	}
	return false
}

const hasTouchCapabilities = () => window.matchMedia('(pointer: coarse)').matches

/**
 * getElementDescription
 *
 * @description try to return a possible label/description of the given element
 * @param {HTMLElement} element
 * @return {string} description
 */
const getElementDescription = (element) => {
	if (!element) return ''

	const elementDescription =
		element.dataset.trackingLabel ||
		element.querySelector('.text')?.textContent ||
		element.dataset.title ||
		element.querySelector('img')?.alt ||
		element.getAttribute('data-label') ||
		(element.childElementCount <= 1 ? element.textContent : '')

	return elementDescription?.replaceAll('\\n', '').trim()
}

/**
 * toSentenceCase
 *
 * @description returns a string with capital letters on first or all words
 * @param {string} str
 * @param {Boolean} onlyFirstWord
 * @return {string} str modified string
 */
const toSentenceCase = (str, onlyFirstWord = false) => {
	if (onlyFirstWord) {
		return (str?.charAt(0).toUpperCase() || '') + str.toLowerCase().slice(1)
	}
	return str
		?.toLowerCase()
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

const formatMoney = (amount, decimalCount = 2, decimal = '.', thousands = ',') => {
	try {
		let amountParsed = amount
		let decimalCountParsed = Math.abs(decimalCount)
		decimalCountParsed = Number.isNaN(decimalCountParsed) ? 2 : decimalCountParsed

		const negativeSign = amount < 0 ? '-' : ''

		const amountParsedString = parseInt(
			(amountParsed = Math.abs(Number(amount) || 0).toFixed(decimalCountParsed)),
			10
		).toString()
		const isThousands = amountParsedString.length > 3 ? amountParsedString.length % 3 : 0

		return (
			negativeSign +
			(isThousands ? amountParsedString.substr(0, isThousands) + thousands : '') +
			amountParsedString.substr(isThousands).replace(/(\d{3})(?=\d)/g, `$1${thousands}`) +
			(decimalCountParsed
				? decimal +
				  Math.abs(amountParsed - amountParsedString)
						.toFixed(decimalCountParsed)
						.slice(2)
				: '')
		)
	} catch (e) {
		return Number.NaN
	}
}

/**
 * getTrackingBaseParams
 *
 * @description returns an Obj with some properties taken from datalayer
 * @return {obj} Obj populated with tracking parameters
 */
const getTrackingBaseParams = () => {
	const obj = {}
	const tc = window.tc_vars
	obj.event = 'virtualPageview'
	obj.usrstatus = tc.user_logged ? 'logged' : 'not logged'
	obj.msn_cgGroup = tc.msn_cgGroup
	obj.rcms_cgGroup = tc.rcms_cgGroup
	obj.emerch_cgGroup = tc.emerch_cgGroup
	obj.usrid = tc.user_Id
	obj.pgcountry = tc.nav_countryCode
	obj.pglanguage = document.documentElement.dataset.lang
	return obj
}

/**
 * isDatalayerFilterDone
 *
 * @description check if some propeties of datalayer are populated, after the func dataLayerFilter has been called.
 * watch CAR::Datalayer::DataLayerFilterCalled for waiting dataLayerFilter to be done
 * @return {Boolean} Boolean, true if datalayer is completely filled
 */
const isDatalayerFilterDone = () =>
	!!window.tc_vars.msn_cgGroup && !!window.tc_vars.rcms_cgGroup && !!window.tc_vars.emerch_cgGroup

const formatProductName = (name) => {
	if (typeof name !== 'string') return ''
	return name.replaceAll('&#', '%%tmpRepl%%').replaceAll('#', '').replaceAll('%%tmpRepl%%', '&#')
}

const formatMfPartNumber = (partnumber, microCategory) => {
	let prdId = partnumber
	if (microCategory === 'rings' || microCategory === 'bracelets') {
		prdId = prdId.substring(0, prdId.length - 2)
	}
	return prdId
}

/**
 * replaceStr
 *
 * @replaceStr Replace a string with a
 * @param {string} str string for replace
 * @param {Array<string>} find An array of String that is to be replaced by replaceable
 * @param {Array<string>} replaceable An array of  String that replaces the substring specified by the specified regexp
 * @return {string} A new string, with some or all matches of a pattern replaced by a replaceable.
 */
const replaceStr = (str, find, replaceable) => {
	let tempStr = str

	find.forEach((elementFind, index) => {
		tempStr = tempStr.replace(new RegExp(elementFind, 'gi'), replaceable[index])
	})

	return tempStr
}

/**
 * formatPrice
 *
 * @description returns a string with the correct delimiters according to the country of navigation
 * @param {string} price the price in which to insert the delimiters
 * @return {string} string with delimiters
 */
const formatPrice = (price) => price.toLocaleString(getSafeCulture())

const uppercaseZip = () => {
	yTos.watch({
		'yTos::AjaxForm::AjaxFormInputValidated': (data) => {
			if (
				data &&
				data.input[0].dataset.ytosAutocompleteTarget?.toLowerCase() === 'postal_code'
			) {
				let zipCodeUppercase = data.input[0]?.value
				zipCodeUppercase = zipCodeUppercase?.toUpperCase()
				data.input[0].value = zipCodeUppercase
			}
		},
	})
}

const overrideZipCodeValidation = () => {
	const isoCodeRegexMap = {
		BR: '^[0-9]{5}[-][0-9]{3}$',
		CH: '^[0-9]{4}$',
		CY: '^[0-9]{4}$',
		CZ: '^[0-9]{3} [0-9]{2}$',
		HR: '^[0-9]{5}$',
		LI: '^[0-9]{4}$',
		MC: '^[0-9]{5}$',
		PL: '^[0-9]{2}[-][0-9]{3}$',
		RO: '^[0-9]{6}$',
		SK: '^[0-9]{3} [0-9]{2}$',
		MT: '^[a-zA-Z]{3} [0-9]{4}$',
		DK: '^[0-9]{4}$',
		GB: '^(([Gg][Ii][Rr] 0[Aa][Aa])|[^QqVv]([0-9]{1,2}|([^IiJjZz][0-9]|[^IiJjZz][0-9]([0-9]|[ABEHMNPRV-Yabehmnprv-y]))|[0-9][A-HJKS-UWa-hjks-uw])\\s{1,1}[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2})$',
		IE: '^(?:[ac-fhknprtv-yAC-FHKNPRTV-Y]{1}[0-9]{2}|[Dd]6[Ww])[ ][0-9ac-fhknprtv-yAC-FHKNPRTV-Y]{4}$',
		NL: '^[0-9]{4}[ ][a-zA-Z]{2}$',
		SE: '^[0-9]{3}[ ][0-9]{2}$',
		GR: '^\\d{3}[ ]\\d{2}$',
		PT: '^[0-9]{4}[-][0-9]{3}$',
	}

	const isoCodesToOverride = Object.keys(isoCodeRegexMap)

	const overrideRegEx = () => {
		document.querySelectorAll('[data-ytos-country-field]').forEach((el) => {
			if (isoCodesToOverride.indexOf(el?.value) === -1) return

			const isoCode = el.value
			const parentForm = el.closest('form')
			const postalcodeField = parentForm.querySelector('.fieldRow.zipCode input')
			// NOTE: edit jQuery object important
			postalcodeField.dataset.valRegexPattern = isoCodeRegexMap[isoCode]
			$(postalcodeField).data('valRegexPattern', isoCodeRegexMap[isoCode])
		})
	}

	yTos.watch({
		'yTos::HelperLoaded::addressbook.editForm': overrideRegEx,
		'yTos::HelperLoaded::addressbook.addForm': overrideRegEx,
		'yTos::HelperLoaded::checkout.billingAddressForm': overrideRegEx,
		'yTos::HelperLoaded::checkout.shippingAddressForm': overrideRegEx,
		'yTos::Addressbook::EditAddressFormUpdated': overrideRegEx,
		'yTos::Addressbook::AddAddressFormUpdated': overrideRegEx,
		'yTos::Checkout::BillingAddressUpdated': overrideRegEx,
		'yTos::Checkout::ShippingAddressUpdated': overrideRegEx,
	})
}

const getJsHelperVersion = (versionsFile, helper) => {
	const data = versionsFile.default
	let version = ''

	data.forEach((el) => {
		if (el.JSHelperName === helper) {
			version = el.Version
		}
	})

	return version
}

const watchAll = (yTosEvents, callback) => {
	const yTosPromises = yTosEvents.map(
		(yTosEvent) =>
			new Promise((resolve) => {
				yTos.watchOne(
					{
						[yTosEvent.notification]: resolve,
					},
					{
						scope: yTosEvent.scope,
					}
				)
			})
	)

	Promise.all(yTosPromises).then(() => {
		const atLeastOneCancelled = yTosPromises
			.map((yTosPromise) => yTosPromise.cancelled)
			.some((cancelled) => cancelled)
		if (!atLeastOneCancelled) {
			callback()
		}
	})

	return yTosPromises
}

const unWatchAll = (yTosPromises) => {
	yTosPromises.forEach((yTosPromise) => {
		yTosPromise.cancelled = true
	})
}

const isAlgoliaPage = () => yTos.navigation?.Action === 'AlgoliaResult'

const getUrlParameter = (name) => {
	const paramName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
	const regex = new RegExp(`[\\?&]${paramName}=([^&#]*)`)
	const results = regex.exec(window.location.search)
	return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

const setCultureFor500 = () => {
	if (sessionStorage.currentCulture) {
		return
	}

	sessionStorage.setItem('currentCulture', `/${yTos.navigation?.Culture}`)
}

const getRandomIdentifier = () =>
	`${Math.floor(Math.random() * (100000 - 1) + 100000)}_${Date.now()}`

const setStylesOnElement = (styles, element) => {
	if (!element) return
	Object.assign(element.style, styles)
}

/**
 * Check if an object object is empty
 *
 * @param {Object} obj
 * @returns {Boolean}
 */
const objectIsEmpty = (obj) => Object.keys(obj).length === 0

/**
 * @returns {Boolean}
 *
 * @memberof Utility
 */
const instantSearchIsOpen = () => {
	const algoliaElement = document.querySelector('algolia-element')
	return algoliaElement?.classList.contains('open')
}

/**
 * Returns a URL with new parameters from a base URL and an object of parameters.
 * This will overwrite existing parameters in newAttrs,
 * and maintain existing parameters that are not in newAttrs.
 *
 * @param {String|URL} originalURL
 * @param {Object} newAttrs
 *
 * @returns {String}
 *
 * @memberof Utility
 */
const generateNewUrl = (originalAnchor, newAttrs) => {
	if (!newAttrs) {
		throw new Error('No new attributes provided')
	}
	if (!originalAnchor || !originalAnchor.origin || !originalAnchor.pathname) {
		throw new Error(
			'Invalid originalURL. You must provide a valid anchor, or URL object, not a string.'
		)
	}

	let queryString = yTos.queryStringToJson(originalAnchor.search.substring(1)) || {}
	let newUrl = originalAnchor.origin + originalAnchor.pathname
	queryString = Object.assign(queryString, newAttrs)
	const queryStringKeys = Object.keys(queryString)
	for (let i = 0; i < queryStringKeys.length; i += 1) {
		const key = queryStringKeys[i]
		const queryEl = queryString[key]
		if (i === 0) {
			newUrl += `?${key}=${encodeURIComponent(JSON.stringify(queryEl))}`
		} else {
			newUrl += `&${key}=${encodeURIComponent(JSON.stringify(queryEl))}`
		}
	}

	if (originalAnchor.hash) {
		newUrl += originalAnchor.hash
	}

	return newUrl
}

/**
 * Returns a string URL with new parameters from a base URL and an object/string of parameters.
 * This will maintain existing parameters as they are and add/override new params without additional encoding.
 * Useful to preserve existent parameters in query string keeping the right formatting.
 *
 * @param {String} baseUrl
 * @param {Object} params
 *
 * @returns {String}
 *
 * @memberof Utility
 */
const addParamToUrl = (baseUrl, params) => {
	const Url = new URL(baseUrl)
	const urlParams = new URLSearchParams(Url.search)
	const paramKeys = Object.keys(params)
	for (let i = 0; i < paramKeys.length; i += 1) {
		const key = paramKeys[i]
		const value = params[key]
		if (value !== undefined) {
			urlParams.set(key, typeof value === 'object' ? JSON.stringify(value) : value)
		}
	}
	Url.search = urlParams.toString()
	return Url.toString()
}

/**
 * @description Returns a string on search searchParams from URL.
 * @param param Parameter to look for in the URL
 * @returns {String}
 *
 * @memberof Utility
 */
const getQueryParam = (param) => {
	const url = new URL(window.location.href)
	const searchParams = url.searchParams

	return searchParams.get(param)
}

/* Native scrollTo with callback
 * @param offset - offset to scroll to
 * @param callback - callback function
 * @param params - params for the callback function
 */
const scrollToWithCB = (offset, callback, params = null) => {
	const fixedOffset = offset.toFixed()
	const onScroll = () => {
		if (window.pageYOffset.toFixed() === fixedOffset) {
			window.removeEventListener('scroll', onScroll)

			if (params) {
				callback(params)
			} else {
				callback()
			}
		}
	}

	window.addEventListener('scroll', onScroll)
	onScroll()
	window.scrollTo({
		top: offset,
		behavior: !yTos.Cartier.devices.prefersReduceMotion ? 'smooth' : 'auto',
	})
}

const moveCookieLayerToPopup = () => {
	document.addEventListener('tc-privacy-wrapper-set', () => {
		if (window.location.pathname.endsWith('cookie-policy')) {
			return
		}

		loadWebComponents(['cookie-popup'])
		yTos.Cartier.cookiesPopupOpen = true
		document.removeEventListener('keyup', window.tC.banner.handleEscape)
		const classToCheck = 'tech-button-on-top'
		const cookieWrapper = document.querySelector('#tc-privacy-wrapper')
		const wrapperContent = cookieWrapper.querySelector('#footer_tc_privacy')
		const techCookiesButton = cookieWrapper.querySelector('#footer_tc_privacy_button_2')
		const cookieTitle = cookieWrapper.querySelector('#description')
		const container = document.createElement('div')
		container.classList.add('car-dialog-container')
		document.body.append(container)

		requestAnimationFrame(() => {
			if (wrapperContent.classList.contains(classToCheck)) {
				cookieTitle.after(techCookiesButton)
			}

			const dialogTemplate = html`<cookie-popup
				class="car-dialog"
				data-scope="cookie-layer"
				data-prevent-close-on-esc="true"
				data-prevent-close-on-bg-click="true"
				data-hide-close-button="true"
				data-prevent-focus-back="true"
				data-append-to-body="true"
				><template>${cookieWrapper}</template>
			</cookie-popup>`

			render(dialogTemplate, container, { window })
		})
	})
}

/**
 * Retrieve all the attributes of an element
 * @returns An object containing all the attributes in a key -> value scheme
 */
const getAllAttributes = (el) =>
	el.getAttributeNames().reduce(
		(obj, name) => ({
			...obj,
			[name]: el.getAttribute(name),
		}),
		{}
	)

/**
 * Initializes all yTos helpers inside a given container
 * @param container the html element or selector that contains new ytos-ctrl elements
 */
const loadYTosHelpers = async (container) => {
	const element = typeof container === 'string' ? document.querySelector(container) : container

	if (!element.querySelector('[data-ytos-ctrl]')) {
		return
	}

	await new Promise((resolve) => {
		const initHelpers = () => {
			yTos.watchOne({
				'yTos::HelpersFinishedLoading': resolve,
			})

			yTos.helpers.initializeHelpers(yTos.domlib(element), {
				shouldInitializeOnlyNew: true,
			})
		}

		if (yTos.helpers.loading === 0) {
			initHelpers()
		} else {
			yTos.watchOne({
				'yTos::HelpersFinishedLoading': initHelpers,
			})
		}
	})
}

const isTableLandscape = () => yTos.Cartier.devices.media['tablet-landscape'].matches

export {
	getSafeCulture,
	getSiblings,
	debounce,
	isVisible,
	getFocusableElements,
	isValidEmail,
	isElementVisible,
	setViewportHeightCssVariable,
	parseHtmlFromString,
	isIOS,
	isSafari,
	hasTouchCapabilities,
	getElementDescription,
	toSentenceCase,
	formatMoney,
	getTrackingBaseParams,
	isDatalayerFilterDone,
	formatProductName,
	formatMfPartNumber,
	replaceStr,
	formatPrice,
	uppercaseZip,
	overrideZipCodeValidation,
	getJsHelperVersion,
	watchAll,
	unWatchAll,
	isAlgoliaPage,
	getUrlParameter,
	setCultureFor500,
	getRandomIdentifier,
	setStylesOnElement,
	objectIsEmpty,
	instantSearchIsOpen,
	generateNewUrl,
	addParamToUrl,
	getQueryParam,
	scrollToWithCB,
	getAllAttributes,
	moveCookieLayerToPopup,
	loadYTosHelpers,
	isTableLandscape,
}
